import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  MapPin, Clock, Users, Star, BookOpen, ChevronLeft, CheckCircle2, HeartHandshake,
  MessageSquare, CalendarDays, Send, Link2, ClipboardCheck, ExternalLink,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { BadgePill } from "@/components/Brand";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";

const BADGE_OPTIONS = ["Welcoming", "Well Prepared", "Great Discussion", "On Time", "Encouraging"];

function fmtDate(iso) {
  try { return new Date(iso).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }); }
  catch { return iso; }
}

function passageLink(ref) {
  const m = /^(.*?) (\d+)/.exec(ref || "");
  if (!m) return null;
  return `/scripture?book=${encodeURIComponent(m[1])}&chapter=${m[2]}`;
}

export default function StudyDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [study, setStudy] = useState(null);
  const [tab, setTab] = useState("overview");

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/studies/${id}`); setStudy(data); }
    catch { toast.error("Study not found"); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const requireAuth = () => { if (!user) { nav("/auth"); return false; } return true; };

  const join = async () => {
    if (!requireAuth()) return;
    try { await api.post(`/studies/${id}/join`); toast.success("You've joined the study"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const leave = async () => {
    try { await api.post(`/studies/${id}/leave`); toast.success("You left the study"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const checkin = () => {
    if (!requireAuth()) return;
    if (!navigator.geolocation) return toast.error("Geolocation not supported");
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        const { data } = await api.post(`/studies/${id}/checkin`, { lat: pos.coords.latitude, lng: pos.coords.longitude });
        toast.success(`Checked in for ${data.session_date}`);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    }, () => toast.error("Location permission denied"));
  };
  const give = async (amount) => {
    try { const { data } = await api.post(`/groups/${id}/donate`, { amount, origin_url: window.location.origin }); window.location.href = data.checkout_url; }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const reportConcern = async () => {
    const text = window.prompt("Describe your concern. Our team reviews every report.");
    if (!text) return;
    try { await api.post(`/groups/${id}/concern`, { text }); toast.success("Thank you — your concern was submitted."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!study) return <div className="mx-auto max-w-4xl px-4 py-16 text-center text-[#6B6763]">Loading…</div>;

  const isMember = study.is_member;
  const isHost = study.is_host;

  return (
    <div className="mx-auto max-w-4xl px-4 py-5 sm:px-6">
      <button onClick={() => nav(-1)} data-testid="back-btn" className="mb-3 flex items-center gap-1 text-sm font-medium text-[#6B6763] hover:text-[#0F3D66]">
        <ChevronLeft size={16} /> Back to the Plaza
      </button>

      {/* Hero */}
      <div className="overflow-hidden rounded-3xl border border-[#E8E4DF] bg-white bp-card-shadow bp-fade-up">
        <div className="relative h-44 sm:h-56">
          <img src="/brand/venue-cafe-inclusive.jpg" alt="An inclusive Bible study group meeting at a public cafe — people of diverse races, ages and abilities, including a wheelchair user and elders, around a table with open Bibles" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/55 to-transparent" />
          <div className="absolute bottom-3 left-4 right-4 flex flex-wrap items-center gap-2">
            <BadgePill tone="primary" className="bg-white/90"><BookOpen size={12} /> {study.book}</BadgePill>
            {study.active
              ? <BadgePill tone="sage" className="bg-white/90"><Users size={12} /> Active group</BadgePill>
              : <BadgePill tone="warn" className="bg-white/90"><Users size={12} /> Needs members</BadgePill>}
          </div>
        </div>
        <div className="p-5 sm:p-6">
          <h1 className="font-display text-2xl font-bold tracking-tight text-[#0F3D66] sm:text-3xl">{study.title}</h1>
          <p className="mt-2 text-sm text-[#6B6763]">{study.description}</p>

          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <Info icon={MapPin} label="Public venue" value={`${study.venue.name} · ${study.venue.category}`} sub={study.venue.address} />
            <Info icon={Clock} label="Schedule" value={`${study.schedule.day_name}s · ${study.schedule.start_time}`} sub={`${study.schedule.frequency} · ${study.schedule.duration_minutes} min`} />
            <Info icon={Users} label="Members" value={`${study.member_count} / min 3${study.capacity ? ` · cap ${study.capacity}` : ""}`} sub={study.active ? "Meeting as scheduled" : "Needs 3 to activate"} />
            <Info icon={Star} label="Host" value={study.host.name} sub={study.host_rating ? `${study.host_rating}★ reputation` : "New host"} />
          </div>

          {study.next_passage && (
            <div className="mt-4 flex items-center gap-3 rounded-2xl bg-[#748C70]/10 p-4">
              <CalendarDays size={20} className="text-[#4f6a4b]" />
              <div className="text-sm">
                <div className="font-semibold text-[#0F3D66]">Up next: {study.next_passage.passage_ref}</div>
                <div className="text-[#6B6763]">{fmtDate(study.next_passage.date)}
                  {passageLink(study.next_passage.passage_ref) && (
                    <button onClick={() => nav(passageLink(study.next_passage.passage_ref))} data-testid="read-next-passage" className="ml-2 font-medium text-[#F36353] hover:underline">Read it →</button>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="mt-5 flex flex-wrap gap-2">
            {!isMember && <Button onClick={join} data-testid="join-btn" className="h-11 rounded-full bg-[#F36353] px-6 text-white hover:bg-[#D94A3D]">Join this study</Button>}
            {isMember && !isHost && <Button onClick={leave} variant="outline" data-testid="leave-btn" className="h-11 rounded-full border-[#E8E4DF] px-6">Leave study</Button>}
            {isMember && <Button onClick={checkin} data-testid="checkin-btn" className="h-11 rounded-full bg-[#748C70] px-6 text-white hover:bg-[#5f7259]"><CheckCircle2 size={17} className="mr-1.5" /> Check in at venue</Button>}
            {isHost && <Button onClick={() => nav(`/host/${id}`)} variant="outline" data-testid="host-toolkit-btn" className="h-11 rounded-full border-[#F36353]/40 px-6 text-[#D94A3D]"><ClipboardCheck size={17} className="mr-1.5" /> Host toolkit</Button>}
            <Button onClick={() => give(2500)} data-testid="give-btn" className="h-11 rounded-full bg-[#0F3D66] px-6 text-white hover:bg-[#0c3253]"><HeartHandshake size={17} className="mr-1.5" /> Give</Button>
            <button onClick={reportConcern} data-testid="report-concern-btn" className="h-11 rounded-full px-4 text-sm font-medium text-[#6B6763] hover:text-[#D94A3D]">Report a concern</button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList className="rounded-full bg-[#F3EEE7] p-1">
          <TabsTrigger value="overview" data-testid="tab-overview" className="rounded-full data-[state=active]:bg-white">Sessions</TabsTrigger>
          <TabsTrigger value="prayer" data-testid="tab-prayer" className="rounded-full data-[state=active]:bg-white">Prayer</TabsTrigger>
          <TabsTrigger value="messages" data-testid="tab-messages" className="rounded-full data-[state=active]:bg-white">Messages</TabsTrigger>
          <TabsTrigger value="ratings" data-testid="tab-ratings" className="rounded-full data-[state=active]:bg-white">Reputation</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <Sessions study={study} />
        </TabsContent>
        <TabsContent value="prayer" className="mt-4">
          <PrayerPanel id={id} isMember={isMember} />
        </TabsContent>
        <TabsContent value="messages" className="mt-4">
          <MessagesPanel id={id} isMember={isMember} isHost={isHost} onSent={load} />
        </TabsContent>
        <TabsContent value="ratings" className="mt-4">
          <RatingsPanel id={id} study={study} isMember={isMember} isHost={isHost} onRated={load} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Info({ icon: Icon, label, value, sub }) {
  return (
    <div className="flex items-start gap-3 rounded-2xl border border-[#F0EBE4] bg-[#FAF7F2] p-3">
      <Icon size={18} className="mt-0.5 shrink-0 text-[#F36353]" />
      <div className="min-w-0">
        <div className="text-xs font-medium uppercase tracking-wide text-[#9c9791]">{label}</div>
        <div className="font-medium text-[#0F3D66]">{value}</div>
        {sub && <div className="truncate text-xs text-[#6B6763]">{sub}</div>}
      </div>
    </div>
  );
}

function Sessions({ study }) {
  const nav = useNavigate();
  return (
    <div className="space-y-2">
      {study.sessions.map((s, i) => (
        <div key={i} data-testid={`session-${i}`} className="flex items-center justify-between rounded-2xl border border-[#E8E4DF] bg-white p-4">
          <div>
            <div className="font-semibold text-[#0F3D66]">{s.passage_ref}</div>
            <div className="text-xs text-[#6B6763]">{fmtDate(s.date)}</div>
          </div>
          {passageLink(s.passage_ref) && (
            <Button variant="outline" size="sm" onClick={() => nav(passageLink(s.passage_ref))} data-testid={`read-session-${i}`} className="rounded-full border-[#E8E4DF]">
              Read <ExternalLink size={13} className="ml-1" />
            </Button>
          )}
        </div>
      ))}
    </div>
  );
}

function PrayerPanel({ id, isMember }) {
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [locked, setLocked] = useState(false);
  const load = useCallback(async () => {
    try { const { data } = await api.get(`/studies/${id}/prayers`); setItems(data.prayers); setLocked(false); }
    catch { setLocked(true); }
  }, [id]);
  useEffect(() => { if (isMember) load(); else setLocked(true); }, [load, isMember]);

  if (locked) return <Gate text="Prayer requests are shared with members only. Join this study to see and add prayers." />;

  const submit = async () => {
    if (!text.trim()) return;
    await api.post(`/studies/${id}/prayers`, { text }); setText(""); load(); toast.success("Prayer request shared");
  };
  const pray = async (pid) => { await api.post(`/prayers/${pid}/pray`); load(); };

  return (
    <div className="space-y-3">
      <div className="rounded-2xl border border-[#E8E4DF] bg-white p-4">
        <Textarea data-testid="prayer-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Share a prayer request with the group…" className="rounded-xl border-[#E8E4DF]" />
        <Button onClick={submit} data-testid="prayer-submit" className="mt-2 rounded-full bg-[#F36353] text-white hover:bg-[#D94A3D]"><HeartHandshake size={16} className="mr-1.5" /> Share request</Button>
      </div>
      {items.map((p) => (
        <div key={p.id} data-testid={`prayer-${p.id}`} className="rounded-2xl border border-[#E8E4DF] bg-white p-4">
          <p className="text-sm text-[#0F3D66]">{p.text}</p>
          <div className="mt-2 flex items-center justify-between text-xs text-[#6B6763]">
            <span>{p.author}</span>
            <button onClick={() => pray(p.id)} data-testid={`pray-${p.id}`} className="flex items-center gap-1 font-medium text-[#F36353]">
              <HeartHandshake size={13} /> Praying · {p.prayed_count}
            </button>
          </div>
        </div>
      ))}
      {items.length === 0 && <Empty text="No prayer requests yet. Be the first to share." />}
    </div>
  );
}

function MessagesPanel({ id, isMember, isHost, onSent }) {
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [locked, setLocked] = useState(false);
  const load = useCallback(async () => {
    try { const { data } = await api.get(`/studies/${id}/messages`); setItems(data.messages); setLocked(false); }
    catch { setLocked(true); }
  }, [id]);
  useEffect(() => { if (isMember) load(); else setLocked(true); }, [load, isMember]);

  if (locked) return <Gate text="Group messages are for members only. Join this study to read updates from the host." />;

  const submit = async () => {
    if (!text.trim()) return;
    try { await api.post(`/studies/${id}/messages`, { text }); setText(""); load(); toast.success("Message sent to members"); onSent?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-3">
      {isHost && (
        <div className="rounded-2xl border border-[#E8E4DF] bg-white p-4">
          <Textarea data-testid="message-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Send an update to all members…" className="rounded-xl border-[#E8E4DF]" />
          <Button onClick={submit} data-testid="message-submit" className="mt-2 rounded-full bg-[#748C70] text-white hover:bg-[#5f7259]"><Send size={15} className="mr-1.5" /> Send to members</Button>
        </div>
      )}
      {items.map((m) => (
        <div key={m.id} data-testid={`message-${m.id}`} className="rounded-2xl border border-[#E8E4DF] bg-white p-4">
          <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-[#D94A3D]"><MessageSquare size={13} /> {m.from}</div>
          <p className="text-sm text-[#0F3D66]">{m.text}</p>
        </div>
      ))}
      {items.length === 0 && <Empty text="No messages yet." />}
    </div>
  );
}

function RatingsPanel({ id, study, isMember, isHost, onRated }) {
  const [items, setItems] = useState([]);
  const [stars, setStars] = useState(5);
  const [badges, setBadges] = useState([]);
  const [comment, setComment] = useState("");
  const [open, setOpen] = useState(false);
  const load = useCallback(async () => { const { data } = await api.get(`/studies/${id}/ratings`); setItems(data.ratings); }, [id]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try { await api.post(`/studies/${id}/rate`, { stars, badges, comment }); setOpen(false); toast.success("Thanks for rating your host"); load(); onRated?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between rounded-2xl border border-[#E8E4DF] bg-white p-4">
        <div>
          <div className="font-display text-lg font-semibold text-[#0F3D66]">{study.host.name}'s reputation</div>
          <div className="flex items-center gap-2 text-sm text-[#6B6763]">
            {study.host_rating ? <><Star size={15} fill="#E0A62B" color="#E0A62B" /> {study.host_rating} · {items.length} ratings</> : "No ratings yet"}
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {Object.entries(study.host_badges || {}).map(([b, c]) => <BadgePill key={b} tone="sage">{b} · {c}</BadgePill>)}
          </div>
        </div>
        {isMember && !isHost && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="rate-host-btn" className="rounded-full bg-[#F36353] text-white hover:bg-[#D94A3D]">Rate host</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display">Rate {study.host.name}</DialogTitle></DialogHeader>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} data-testid={`star-${n}`} onClick={() => setStars(n)}>
                    <Star size={28} fill={n <= stars ? "#E0A62B" : "none"} color={n <= stars ? "#E0A62B" : "#CBC6BF"} />
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap gap-2">
                {BADGE_OPTIONS.map((b) => (
                  <button key={b} data-testid={`badge-${b}`} onClick={() => setBadges(badges.includes(b) ? badges.filter((x) => x !== b) : [...badges, b])}
                    className={`rounded-full px-3 py-1.5 text-xs font-medium ${badges.includes(b) ? "bg-[#748C70] text-white" : "bg-[#F3EEE7] text-[#6B6763]"}`}>{b}</button>
                ))}
              </div>
              <Textarea data-testid="rating-comment" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Optional comment" className="rounded-xl border-[#E8E4DF]" />
              <Button onClick={submit} data-testid="rating-submit" className="rounded-full bg-[#F36353] text-white hover:bg-[#D94A3D]">Submit rating</Button>
            </DialogContent>
          </Dialog>
        )}
      </div>
      {items.map((r, i) => (
        <div key={i} data-testid={`rating-${i}`} className="rounded-2xl border border-[#E8E4DF] bg-white p-4">
          <div className="flex items-center gap-2">
            <span className="flex items-center gap-0.5">{[...Array(r.stars)].map((_, k) => <Star key={k} size={14} fill="#E0A62B" color="#E0A62B" />)}</span>
            <span className="text-sm font-medium text-[#0F3D66]">{r.author}</span>
          </div>
          {r.comment && <p className="mt-1 text-sm text-[#6B6763]">{r.comment}</p>}
          <div className="mt-2 flex flex-wrap gap-1.5">{r.badges.map((b) => <BadgePill key={b} tone="sage">{b}</BadgePill>)}</div>
        </div>
      ))}
    </div>
  );
}

const Gate = ({ text }) => <div className="rounded-2xl border border-dashed border-[#E8E4DF] bg-white p-8 text-center text-sm text-[#6B6763]">{text}</div>;
const Empty = ({ text }) => <div className="rounded-2xl border border-dashed border-[#E8E4DF] bg-white p-6 text-center text-sm text-[#6B6763]">{text}</div>;
